/**
 * @Author: v_jppjpeng
 * @Date: 2024-01-05 11:37:37
 * @Explain: 时间格式化
 */

import type { Moment } from 'moment';
import moment from 'moment';
import { getSystemTimezone } from 'smartlink-format-time';
// utils
import { insertStr } from '@/utils/common';
import { formatShowTime } from '@/utils/common/businessLogic';
import { getKeyValuePairs } from '@/utils/common/dataConversion';
import { isNumber } from '@/utils/common/dataJudgment';
import { timeFormatOptions } from '@/utils/common/options';
import { getLocalStorage } from '@/utils/global';
import { CHINESE_TIME_RANGES, defaultDateFormat } from './const';
// type
import type { TimeFormatOptionsProps } from '@/definition/common/data.d';
import {
  AbbreviationLanguageEnum,
  LanguageEnum,
  StoreKeyEnum,
} from '@/definition/common/data.d';
import type { DiffType } from './data.d';

/**
 * 格式化时间文本
 * @param match 匹配到的时间标识符 ('am' 或 'pm')
 * @param offset 匹配位置在原始字符串中的偏移量
 * @param originalString 原始字符串
 * @param isChinese 是否为中文环境
 * @param fullTime
 * @returns 格式化后的时间描述
 */
const formatTimeText = (
  match: string,
  offset: number,
  originalString: string,
  isChinese: boolean,
  fullTime: string,
): string => {
  const hour = moment(fullTime, 'hh:mm').hour();
  if (isChinese) {
    return getChineseTimeDescription(match.toLowerCase(), hour);
  }

  return getEnglishTimeDescription(
    match.toLowerCase(),
    fullTime,
    originalString.trim(),
    match,
  );
};

/**
 * 获取中文时间描述
 */
const getChineseTimeDescription = (period: string, hour: number): string => {
  if (period === 'am') {
    // AM: 12-5点为凌晨（12小时制 12点-5点），6-11点为早上
    return hour >= 6 && hour <= 11
      ? CHINESE_TIME_RANGES.AM.MORNING
      : CHINESE_TIME_RANGES.AM.EARLY_MORNING;
  }

  // PM: 12点为中午，1-5点为下午，6-11点为晚上
  if (hour === 12) {
    return CHINESE_TIME_RANGES.PM.NOON;
  }

  return hour <= 5
    ? CHINESE_TIME_RANGES.PM.AFTERNOON
    : CHINESE_TIME_RANGES.PM.EVENING;
};

/**
 * 获取英文时间描述
 * 需要特殊处理 12:00 am 为午夜 12:00 pm 为正午
 */
const getEnglishTimeDescription = (
  period: string,
  timePart: string,
  originalString: string,
  match: string,
): string => {
  // 特殊处理正午和午夜
  let formattedTime = '';
  const m = moment(originalString);
  if (m.isValid()) {
    // 验证通过代表传递进来的时完整时间 例如【24 Dec 2025 12:00】 【表格时间】
    formattedTime = m?.format?.('hh:mm');
  } else {
    // 不完整的时间 例如 【12:00 am】 一般是活动的时间下拉列表
    formattedTime = timePart;
  }
  if (formattedTime === '12:00') {
    return period === 'am' ? 'AM (Midnight)' : 'PM (Noon)';
  }

  // 其他情况返回原始标识（保持大写）
  return match.toUpperCase();
};

// 将am替换为上午、pm替换为下午 (不区分am、pm大小写)
export const formattedTime = (timeStr: string = '') => {
  // 获取存储语言设置，例如zh-CN
  const localLanguage = getLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE);
  // 获取统一格式化的时间 例如 【2025-12-24 am 12:00】 （中文时间格式）
  const momentDate = moment(timeStr, 'YYYY-MM-DD a hh:mm', true);
  let time12 = '';
  let fullTime = '';
  if (momentDate.isValid()) {
    // 中文格式验证通过 处理中文时间格式
    const currentFormat = 'YYYY-MM-DD a hh:mm';
    time12 = momentDate.locale('en').format(currentFormat);
    fullTime = moment(time12, currentFormat).format('hh:mm');
  } else {
    // 验证不通过 不是完整时间 例如 【12:00 am】 一般是活动的时间下拉列表 或者是英文时间的格式这里
    time12 = timeStr;
    fullTime = moment(time12, 'hh:mm a').format('hh:mm');
  }
  // 中文环境 am显示上午 pm显示下午
  return time12.replace(/(am|pm)/gi, (match, group, offset, originalString) =>
    formatTimeText(
      match,
      offset,
      originalString,
      localLanguage === LanguageEnum.ZH,
      fullTime,
    ),
  );
};

/**
 * 中文情况下反转时间说明
 * 例如：12:31 中午 => 中午 12:31
 * **/
export const reverseTimeAndText = (timeStr: string) => {
  const result = formattedTime(timeStr);
  // 获取存储语言设置，例如zh-CN
  const localLanguage = getLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE);
  if (localLanguage === LanguageEnum.ZH) {
    // 仅在中文反转
    return result.split(' ').reverse()?.join(' ');
  }
  // 非中文直接返回
  return result;
};

/**
 * 获取时间格式
 */
export const getDateTimeFormat = () => {
  const timeFormatType = getLocalStorage(StoreKeyEnum.TIME_FORMAT_TYPE) || '2';
  const timeFormatJson = getKeyValuePairs({
    list: timeFormatOptions,
    key: 'value',
    value: 'allData',
  });
  return timeFormatJson[+timeFormatType] as TimeFormatOptionsProps;
};

/**
 * 常规 Start
 */
/** 获取传入时间的时间戳 - 毫秒 */
export const getDateTimeStamp = (val?: string) =>
  val ? new Date(val).getTime() : new Date().getTime();

// 是否秒的值
export const isSecondsValue = (value: string) => value.length <= 10;

// 时间戳转化(单位：毫秒)
export const onTimeStampConversion = (value: number) => {
  const newValue = `${value}`;
  return isSecondsValue(newValue) ? value * 1000 : value;
};

// 生成时分选项
export function generateTimeData({
  minuteInterval = 15,
}: {
  minuteInterval?: number;
}) {
  const data = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += minuteInterval) {
      const twelveHour = hour > 12 ? hour - 12 : hour;
      // 12小时制 - 不足两位补0
      let twelveHourText = twelveHour.toString().padStart(2, '0');
      // 24小时制 - 不足两位补0
      const twentyFourHourText = hour.toString().padStart(2, '0');
      // 分钟 - 不足2位补0
      const minuteText = minute.toString().padStart(2, '0');
      // 秒
      const secondText = '00';
      // 零点展示为12点（因为12小时制没有00）
      if (twelveHourText === '00') {
        twelveHourText = '12';
      }
      // 获取存储语言设置，例如zh-CN
      const localLanguage = getLocalStorage(StoreKeyEnum.LOCAL_LANGUAGE);
      // 12小时制
      let twelveHourTime =
        localLanguage === LanguageEnum.ZH
          ? `${hour < 12 ? 'am' : 'pm'} ${twelveHourText}:${minuteText}`
          : `${twelveHourText}:${minuteText} ${hour < 12 ? 'am' : 'pm'}`;
      // 处理am、pm 中文显示上午、下午
      twelveHourTime = formattedTime(twelveHourTime);

      // 24小时制
      const twentyFourHourTime = `${twentyFourHourText}:${minuteText}:${secondText}`;

      data.push({ text: twelveHourTime, value: twentyFourHourTime });
    }
  }
  return data;
}

/**
 * 常规 End
 */

/**
 * Moment Start
 */
/** 获取当前时区信息 */
export const getNowZone = () => {
  const zone: number = moment().utcOffset() / 60;
  let c = '';
  // 判断当前时区是正数时区或负数时区
  if (zone >= 0) c = '+';

  // 获取当地所处时区GMT信息 - 时区这里有夏令时,冬令时,负数的情况时,夏令时会比系统时区快一个小时
  const timeInfo = new Date().toTimeString().split(' ');
  timeInfo.shift();
  timeInfo.pop();
  let timezoneString = timeInfo.join(' ');
  timezoneString = timezoneString.replace('GMT', '').split(' (')[0];
  const halfFormatUtcZoneStr: string = insertStr(timezoneString, 3, ':');

  return {
    /** eg: UTC+8.75 */
    decimalFormatUtcZone: `UTC${c}${String(zone)}`,
    /** eg: UTC+08:45 */
    halfFormatUtcZone: `UTC${halfFormatUtcZoneStr}`,
    /** eg: 8.75 */
    decimalZone: zone, //
    /** eg: +08:45 */
    halfUtcZone: halfFormatUtcZoneStr,
  };
};

/** 格式化日期特别显示情况 => YYYY-MM-DD HH:mm */
export const scDateFormat = (params?: {
  /** 自定义显示的时间（年月日，时分秒）格式 */
  dateFormat?: string;
  /** 自定义显示的时间（时分秒）格式 */
  timeFormat?: string;
  /** 是否显示时分秒 */
  showHMS?: boolean;
}) => {
  // 中英 时间展示格式
  const scDefaultDateFormat = getDateTimeFormat().componentFormat;

  const {
    dateFormat = null,
    showHMS = true,
    timeFormat = 'HH:mm',
  } = params || {};
  // 显示自定义时间格式
  if (dateFormat)
    return timeFormat ? `${dateFormat} ${timeFormat}` : dateFormat;

  // 使用默认规则 带有时分秒
  if (showHMS) return `${scDefaultDateFormat} ${timeFormat}`;

  return scDefaultDateFormat;
};

/** 时间戳时间格式化 */
export const timeStampFormat = (
  value: any,
  dateFormat = defaultDateFormat,
): string => (value && dateFormat ? moment.unix(value).format(dateFormat) : '');

/** 时间戳格式化为标准时间格式输出 2024-07-17T17:30:08+08:00  */
export const timeStampStandardFormat = (value: any): string =>
  isNumber(value) ? moment.unix(value).format() : '';

/** moment时间格式化 */
export const momentDateFormat = (
  value: any,
  dateFormat = defaultDateFormat,
): string => (value && dateFormat ? moment(value).format(dateFormat) : '');

/** moment转时间戳 - 毫秒 */
export const getTimeStamp = (value: Moment | Date | string) =>
  moment(value).valueOf();

/** moment 转成本地时区时间 */
export const getLocalDate = (value: Moment | Date | string) =>
  moment(value).local();

/** moment - 计算日期差值 */
export const onDateCalcDiff = ({
  startTime,
  endTime,
  diffType,
}: {
  startTime: any;
  endTime: any;
  diffType: DiffType;
}) => moment(endTime).diff(startTime, diffType, true);

/** 12:00:00 转成对象
 *  {
      hour: 12,
      minute: 0,
      second: 0,
      millisecond: 0,
    }
*/
export const onParseTime = (timeString: string) => {
  const time = moment(timeString, 'HH:mm:ss');

  return {
    hour: time.get('hour'),
    minute: time.get('minute'),
    second: time.get('second'),
    millisecond: 0, // 默认毫秒为0
  };
};
/** 计算时间差值 */
export const onTimeCalcDiff = ({
  startTime,
  endTime,
}: {
  startTime: string;
  endTime: string;
}) => {
  const params = {
    hours: 0,
    minutes: 0,
    flag: true,
  };
  // 解析时间字符串
  const today = moment().format('YYYY-MM-DD');
  const time1 = moment(`${today}${startTime}`, 'YYYY-MM-DD HH:mm');
  let time2 = moment(`${today}${endTime}`, 'YYYY-MM-DD HH:mm');
  let diffMs = 0;

  if (time1 && time2) {
    diffMs = time2.diff(time1);
    // 计算跨天
    if (diffMs < 0) {
      time2 = time2.add(1, 'days');
      diffMs = time2.diff(time1);
    }
  } else {
    params.flag = false;
  }
  // 如果时间差为负数，取绝对值
  const absDiffMs = Math.abs(diffMs);
  // 转换为小时和分钟
  params.hours = Math.floor(absDiffMs / (1000 * 60 * 60)) || 0;
  const remainingMs = absDiffMs % (1000 * 60 * 60);
  params.minutes = Math.round(remainingMs / (1000 * 60)) || 0;

  // 判断输出格式
  return params;
};

// 日期比较
export const onDateContrast = ({ a, b }: { a: any; b: any }) => {
  if (a && b) {
    const aTime = isNumber(a) ? onTimeStampConversion(a) : getTimeStamp(a);
    const bTime = isNumber(b) ? onTimeStampConversion(b) : getTimeStamp(b);

    if (aTime > bTime) {
      return false;
    }
  }

  return true;
};

// 时间比较
export const onTimeContrast = ({ a, b }: { a: any; b: any }) => {
  if (a && b) {
    const aTime = moment(a, 'HH:mm:ss');
    const bTime = moment(b, 'HH:mm:ss');

    if (aTime > bTime) {
      return false;
    }
  }

  return true;
};

// moment 设置指定时间
export const setSpecifiedTime = (
  date: any = new Date(),
  options: { hour?: number; minute?: number; seconds?: number },
) => moment(date).set(options);

// 获取时分秒
export const getTimeHms = (val: string | Moment) => {
  if (val) {
    const date = moment(val);
    const hour = date.format('HH') || '00';
    const minute = date.format('mm') || '00';
    const second = '00';
    return `${hour}:${minute}:${second}`;
  }
};

// 拼接日期-时间
export const onSplicingTime = (
  date: Moment = moment(),
  time = '00:00:00',
  config?: {
    format?: string;
  },
) => {
  const { format = 'YYYY-MM-DD' } = config || {};

  const newDate = date ? moment(date) : moment();
  const newVal = `${newDate.format(format)} ${time}`;
  return newVal;
};
/** 获取最开始时间 */
export const getTimeStartOf = (
  val: any,
  unitOfTime: moment.unitOfTime.StartOf = 'days',
) => val && moment(val).startOf(unitOfTime);

/** 获取最后时间 */
export const getTimeEndOf = (
  val: any,
  unitOfTime: moment.unitOfTime.StartOf = 'days',
) => val && moment(val).endOf(unitOfTime);

/**
 * Moment End
 */

// 时间选择中英文环境年月日位置输出调整
export const timePinkYMD = (time: string) => {
  const localLanguage = getLocalStorage(StoreKeyEnum.DESIGN_LANGUAGE);
  const [day, month, year] = time.split('-');
  const outputDate: any =
    localLanguage === AbbreviationLanguageEnum.EN
      ? `${year}-${month}-${day}`
      : time;

  return outputDate;
};

// 截取xxxx-xx-xx
export const getDateStr = (val: string) => {
  return val?.split(' ')[0];
};
// 截取00:00:00
export const getTimeStr = (val: string) => {
  return val?.split(' ')[1];
};
// 时间拼接
export const onSpliceDateTime = (ymd: string, hms: string) => {
  return ymd && hms ? `${ymd} ${hms}` : '';
};

// 获取当前浏览(即：操作系统)UTC时区信息
export const getCurrentSystemTimezone = () => {
  // 获取当前时间
  const now = new Date();

  // 获取本地时间与UTC时间的差异（以分钟为单位）
  const timezoneOffset = now.getTimezoneOffset();

  // 将差异转换为小时和分钟
  const hours = Math.floor(Math.abs(timezoneOffset) / 60);
  const minutes = Math.abs(timezoneOffset) % 60;

  // 构造时区字符串，例如 "UTC-XX:XX"
  const timezoneString = `UTC${timezoneOffset > 0 ? '-' : '+'}${String(
    hours,
  ).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

  // 返回时区字符串
  return timezoneString;
};

// 获取操作系统当前时间转化为sml管理端设置的时区对应时间日期的开始及结束时分秒
export const getCurrentSystemTimeToSmlTime = () => {
  // 获取当前操作系统时区的当前时间
  let defaultStartTime = moment().format(defaultDateFormat);
  // 获取当前浏览(即：操作系统)UTC时区信息
  const CurrentSystemTimezone = getCurrentSystemTimezone();
  // 获取sml管理端设置的时区信息
  const smlConsoleSetTime = getSystemTimezone()
    ?.toLocaleLowerCase()
    ?.replace('utc', '');
  // 将操作系统当前时间转为sml设置的时区对应时间
  const currentTime: any = formatShowTime(defaultStartTime, {
    dateFormat: defaultDateFormat,
    optsInputTimeZone: CurrentSystemTimezone,
  });
  // 获取开始时间日期的起点时分秒
  defaultStartTime = momentDateFormat(getTimeStartOf(currentTime));
  // 获取开始时间日期的最后一刻时分秒
  let defaultEndTime = momentDateFormat(getTimeEndOf(currentTime));
  // 将开始时间字符串处理为标准时间格式形式
  defaultStartTime = defaultStartTime.replace(' ', 'T') + smlConsoleSetTime;
  // 将结束时间字符串处理为标准时间格式形式
  defaultEndTime = defaultEndTime.replace(' ', 'T') + smlConsoleSetTime;

  return {
    defaultStartTime,
    defaultEndTime,
    currentTime,
  };
};
